import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { FilterSelect } from '../../../components/ui';
import {
  SearchInputIc,
  ResetFiltersIc,
  DangerIc,
} from '../../../components/icons';
import { RevshareOffersTable } from './components/RevshareOffersTable';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import {
  refShareApi,
  RefShareOffer,
  OfferStatus,
  OwnerType,
  GetRefShareOffersParams,
} from '../../../api/services';
import { showError, showPartnerSuccess } from '../../../utils/toast';
import { parseApiError } from '../../../utils/errorParser';

const RevshareOffersPage: React.FC = () => {
  const { t } = useTranslations();

  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: 'RevShare Офферы', isActive: true },
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State
  const [offers, setOffers] = useState<RefShareOffer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [localSearch, setLocalSearch] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<string>('all');
  const [ownerTypeFilter, setOwnerTypeFilter] = useState<string>('all');

  // Status options matching backend: moderation, active, hidden, refusal
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareOffers.filters.status.all') },
    { key: 'moderation', label: t('admin.revshareOffers.filters.status.moderation') },
    { key: 'active', label: t('admin.revshareOffers.filters.status.active') },
    { key: 'hidden', label: t('admin.revshareOffers.filters.status.inactive') },
    // { key: 'refusal', label: t('admin.revshareOffers.filters.status.banned') },
  ], [t]);

  // Campaign type options: free, free trial
  const campaignTypeOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareOffers.filters.campaignType.all') },
    { key: 'free', label: 'FREE' },
    { key: 'free trial', label: 'Free-trial' },
  ], [t]);

  // Owner type options
  const ownerTypeOptions = useMemo(() => [
    { key: 'all', label: 'Все владельцы' },
    { key: 'creator', label: t('admin.revshareOffers.filters.owner.creator') },
    { key: 'agency', label: t('admin.revshareOffers.filters.owner.agency') },
  ], [t]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(localSearch);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, campaignTypeFilter, ownerTypeFilter]);

  // Helper to check if filter value is valid (not 'all', undefined, or empty)
  const isValidFilter = (value: string | undefined | null): boolean => {
    return !!value && value !== 'all' && value !== 'undefined';
  };

  // Fetch offers
  const fetchOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetRefShareOffersParams = {
        page: currentPage,
        page_size: pageSize,
        sort_by: 'created_at',
      };

      if (search) {
        params.search = search;
      }

      if (isValidFilter(statusFilter)) {
        params.status = statusFilter as OfferStatus;
      }

      if (isValidFilter(campaignTypeFilter)) {
        params.type = campaignTypeFilter as 'free' | 'free trial';
      }

      if (isValidFilter(ownerTypeFilter)) {
        params.owner_type = ownerTypeFilter as OwnerType;
      }

      const response = await refShareApi.getOffers(params);
      setOffers(response.objects || []);
      setTotalCount(response.count || 0);
    } catch (error: any) {
      console.error('Failed to load offers:', error);
      showError(parseApiError(error, 'Не удалось загрузить офферы'));
      setOffers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, search, statusFilter, campaignTypeFilter, ownerTypeFilter]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Handle status change
  const handleStatusChange = useCallback(async (modelId: string, newStatus: OfferStatus) => {
    try {
      await refShareApi.updateOfferStatus(modelId, newStatus);

      // Update local state
      setOffers((prev) =>
        prev.map((offer) => {
          const offerId = offer._id || offer.id;
          if (offerId === modelId) {
            return {
              ...offer,
              offer: offer.offer ? { ...offer.offer, status: newStatus } : { status: newStatus } as any,
            };
          }
          return offer;
        })
      );

      showPartnerSuccess(
        newStatus === 'active'
          ? 'Оффер успешно активирован'
          : 'Оффер успешно деактивирован'
      );
    } catch (error: any) {
      console.error('Failed to update offer status:', error);
      showError(parseApiError(error, 'Не удалось изменить статус оффера'));
    }
  }, []);

  // Reset filters
  const handleReset = useCallback(() => {
    setLocalSearch('');
    setSearch('');
    setStatusFilter('all');
    setCampaignTypeFilter('all');
    setOwnerTypeFilter('all');
  }, []);

  const hasActiveFilters = localSearch !== '' ||
    statusFilter !== 'all' ||
    campaignTypeFilter !== 'all' ||
    ownerTypeFilter !== 'all';

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-1">
        <div className="px-[24px] py-[24px]">
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              Офферы RevShare 
            </Typography>

            <div className="flex items-center gap-[16px] flex-wrap">
              {/* Search by owner name or offer ID */}
              <Input
                type="text"
                placeholder="Поиск по ID оффера"
                size="sm"
                value={localSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearch(e.target.value)}
                endContent={<SearchInputIc />}
                classNames={{
                  mainWrapper: "w-[320px]",
                  base: "w-[320px]",
                  input: "text-sm",
                  inputWrapper: "rounded-lg h-[36px] w-[320px]",
                }}
              />

              {/* Filter by status */}
              <FilterSelect
                selectedKey={statusFilter}
                onSelectionChange={(val) => setStatusFilter(val || 'all')}
                options={statusOptions}
                placeholder={t('admin.revshareOffers.filters.status.placeholder')}
                variant="campaign"
                autoWidth={true}
                startContent={<DangerIc size={20} />}
              />

              {/* Filter by campaign type */}
              <FilterSelect
                selectedKey={campaignTypeFilter}
                onSelectionChange={(val) => setCampaignTypeFilter(val || 'all')}
                options={campaignTypeOptions}
                placeholder={t('admin.revshareOffers.filters.campaignType.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              {/* Filter by owner type */}
              <FilterSelect
                selectedKey={ownerTypeFilter}
                onSelectionChange={(val) => setOwnerTypeFilter(val || 'all')}
                options={ownerTypeOptions}
                placeholder={t('admin.revshareOffers.filters.owner.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              {/* Reset filters button */}
              {hasActiveFilters && (
                <Tooltip content="Сбросить фильтры" placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleReset}
                  >
                    <ResetFiltersIc />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <RevshareOffersTable
            offers={offers}
            isLoading={isLoading}
            pagination={{
              count: totalCount,
              page_size: pageSize,
              page: currentPage,
            }}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            onStatusChange={handleStatusChange}
          />
        </div>
      </div>
    </div>
  );
};

export default RevshareOffersPage;
